import type { Brand } from '~/@entities/Brand'
import type {
  AddWorksheetAssignScheduleTargetRequest,
  AddWorksheetAssignScheduleTargetResponse,
  CancelWorksheetAssignScheduleTargetRequest,
  CommonCloudFrontUrlResponse,
  CommonResponse,
  CommonResponseCollectionWorksheetDesignTypeResponse,
  CommonResponseListAssignResponse,
  CommonResponseSliceAcademyWorksheetResponse,
  CommonResponseSliceArithmeticWorksheetResponse,
  CommonResponseSliceChapterWorksheetResponse,
  CommonResponseSliceEntranceWorksheetResponse,
  CommonResponseSliceExamWorksheetResponse,
  CommonResponseSliceGroupedArithmeticWorksheetResponse,
  CommonResponseSlicePracticeTestWorksheetResponse,
  CommonResponseSliceSchoolPrepareTextbookWorksheetResponse,
  CommonResponseSliceSchoolPrepareWorksheetResponse,
  CommonResponseSliceWeeklyWorksheetResponse,
  CommonResponseSliceWorksheetDesignResponse,
  CommonResponseWorksheetDesignDetailResponse,
  CopyCustomWorksheetData,
  CreateWorksheetAssignScheduleRequest,
  CreateWorksheetAssignScheduleResponse,
  CreateWorksheetDesignRequest,
  CustomWorksheetCopyRequest,
  DownloadV2Params,
  EmailRequest,
  EmailSendData,
  GetArithmeticWorkSheetListParams,
  GetChapterWorksheetParams,
  GetCustomWorksheetsByParams,
  GetEntranceWorksheetParams,
  GetExamWorksheetParams,
  GetGroupedArithmeticWorksheetParams,
  GetHwpPurchaseHistoryParams,
  GetPracticeTestWorksheetParams,
  GetSatWorksheetParams,
  GetSchoolPrepareEssayWorksheetParams,
  GetSchoolPrepareTextbookWorksheetParams,
  GetSchoolPrepareWorksheetParams,
  GetSchoolTestWorksheetBySchoolParams,
  GetSchoolTestWorksheetCountBySchoolParams,
  GetUniversityPracticeTestWorksheetPairParams,
  GetUniversityPracticeTestWorksheetParams,
  GetWeeklyWorksheetParams,
  GetWorksheetAssignScheduleDetailsParams,
  GetWorksheetDesignParams,
  HwpPurchaseHistoryResponse,
  HwpUnitPriceResponse,
  ModifyWorksheetAccessModifierRequest,
  OriginalWorksheetHwpDownloadQuotaResponse,
  OriginalWorksheetQuotaResponse,
  Pageable,
  RestoreWorksheetRequest,
  SchoolTestSimilarWorksheetCountResponse,
  SchoolTestSimilarWorksheetResponse,
  SliceSatWorksheetResponse,
  SliceSchoolPrepareEssayWorksheetResponse,
  SliceUniversityPracticeTestWorksheetResponse,
  SliceWorksheetAssignScheduleDetailResponse,
  UpdateWorksheetAssignScheduleRequest,
  ValidateWorksheetAssignScheduleRequest,
  WorksheetAssignScheduleCountResponse,
  WorksheetAssignScheduleTargetResponse,
  WorksheetCaptchaChallengeResponse,
  WorksheetCaptchaVerifyRequest,
  WorksheetCaptchaVerifyResponse,
  WorksheetHwpCreationRequest,
  WorksheetHwpCreationResponse,
  WorksheetHwpDownloadResponse,
} from '~/api-spec/__generated__/main'
import type { SearchData, SearchParams } from '~/api-spec/__generated__/search'
import { httpClient, type HttpFile, type RequestMeta } from '~/http/client'
import type { WithDefaultedOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import {
  adaptArithmeticWorksheetGroups,
  adaptArithmeticWorksheets,
  adaptLeveledWorksheets,
  adaptMyWorksheets,
  adaptOriginalWorksheetQuota,
  adaptPracticeTestWorksheets,
  adaptSatWorksheets,
  adaptSchoolTestSimilarWorksheetCount,
  adaptSchoolTestSimilarWorksheets,
  adaptSharedCustomWorksheets,
  adaptTextbookWorksheets,
  adaptWorksheetAssignees,
  adaptWorksheetAssignScheduleCount,
  adaptWorksheetAssignScheduleCreation,
  adaptWorksheetAssignSchedules,
  adaptWorksheetAssignScheduleTargetCount,
  adaptWorksheetAssignScheduleTargets,
  adaptWorksheetBrand,
  adaptWorksheetCaptchaChallenge,
  adaptWorksheetCaptchaVerification,
  adaptWorksheetDesignDetail,
  adaptWorksheetDesigns,
  adaptWorksheetDesignTemplates,
  adaptWorksheetFile,
  adaptWorksheetHwpFiles,
  adaptWorksheetHwpPurchaseHistory,
  adaptWorksheetHwpQuota,
  adaptWorksheetHwpRequest,
  adaptWorksheetHwpUnitPrice,
  adaptWorksheetId,
} from './adapters'
import type {
  ArithmeticWorksheet,
  ArithmeticWorksheetGroup,
  LeveledWorksheetListItem,
  MyWorksheetListItem,
  OriginalWorksheetQuota,
  PracticeTestWorksheetListItem,
  SatWorksheetListItem,
  SchoolTestSimilarWorksheet,
  SchoolTestSimilarWorksheetCount,
  SharedCustomWorksheet,
  TextbookWorksheetListItem,
  Worksheet,
  WorksheetAssignee,
  WorksheetAssignSchedule,
  WorksheetAssignScheduleCount,
  WorksheetAssignScheduleCreation,
  WorksheetAssignScheduleTarget,
  WorksheetCaptchaChallenge,
  WorksheetCaptchaVerification,
  WorksheetDesign,
  WorksheetDesignDetail,
  WorksheetDesignTemplate,
  WorksheetFile,
  WorksheetHwpFile,
  WorksheetHwpPurchaseHistory,
  WorksheetHwpQuota,
  WorksheetHwpRequest,
  WorksheetHwpUnitPrice,
} from './schemas'

export type MyWorksheetListParams = GetCustomWorksheetsByParams
export type SchoolPrepareWorksheetListParams = GetSchoolPrepareWorksheetParams
export type SchoolPrepareEssayWorksheetListParams = GetSchoolPrepareEssayWorksheetParams
export type TextbookWorksheetListParams = GetSchoolPrepareTextbookWorksheetParams
export type PracticeTestWorksheetListParams = GetPracticeTestWorksheetParams
export type EntranceTestWorksheetListParams = GetEntranceWorksheetParams
export type ChapterTestWorksheetListParams = GetChapterWorksheetParams
export type WeeklyTestWorksheetListParams = GetWeeklyWorksheetParams
export type ExamWorksheetListParams = GetExamWorksheetParams
export type SatWorksheetListParams = GetSatWorksheetParams
export type UniversityPracticeTestWorksheetListParams = GetUniversityPracticeTestWorksheetParams
export type UniversityPracticeTestPairWorksheetListParams =
  GetUniversityPracticeTestWorksheetPairParams
export type ArithmeticWorksheetListParams = GetArithmeticWorkSheetListParams
export type ArithmeticWorksheetGroupListParams = GetGroupedArithmeticWorksheetParams
export type SchoolTestSimilarWorksheetParams = GetSchoolTestWorksheetBySchoolParams
export type SchoolTestSimilarWorksheetCountParams = GetSchoolTestWorksheetCountBySchoolParams
// 기출과 비슷한 학습지를 찾는 범위(같은 학교·지역·인근 지역·특수교육 지역)
export type SchoolTestSearchType = SchoolTestSimilarWorksheetParams['searchType']
export type WorksheetAssignScheduleValidationBody = ValidateWorksheetAssignScheduleRequest
export type WorksheetAssignScheduleListParams = Omit<
  GetWorksheetAssignScheduleDetailsParams,
  'pageable'
> &
  Pageable
export type WorksheetAssignScheduleBody = CreateWorksheetAssignScheduleRequest
export type WorksheetAssignScheduleTimeBody = UpdateWorksheetAssignScheduleRequest
export type WorksheetAssignScheduleStudentsAddBody = AddWorksheetAssignScheduleTargetRequest
export type WorksheetAssignScheduleStudentsCancelBody = CancelWorksheetAssignScheduleTargetRequest
export type WorksheetAccessModifierBody = ModifyWorksheetAccessModifierRequest
export type WorksheetCopyBody = CustomWorksheetCopyRequest
export type WorksheetFileParams = DownloadV2Params
export type WorksheetDesignListParams = GetWorksheetDesignParams
export type WorksheetDesignBody = CreateWorksheetDesignRequest
export type WorksheetCaptchaAnswer = WorksheetCaptchaVerifyRequest
export type WorksheetHwpPurchaseHistoryParams = GetHwpPurchaseHistoryParams
// 서버는 templateType·color 에 기본값(DEFAULT·BLUE)을 두어 생략을 받는데 스펙은 필수로 그린다.
// 2026-10-08 백엔드 origin/develop(08ff3525b) 의 worksheet/controller/request/WorksheetHwpCreationRequest.kt 로 확인.
export type WorksheetHwpBody = WithDefaultedOptionals<
  WorksheetHwpCreationRequest,
  'templateType' | 'color'
>

/***************** 학습지 삭제·복원 *****************/

// 학습지를 휴지통으로 옮긴다
export const deleteWorksheets = async (
  worksheetIds: Worksheet['id'][],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<CommonResponse>('/worksheet', { data: worksheetIds, meta })
}

// 학원그룹 공유 학습지를 휴지통을 거치지 않고 지운다
export const forceDeleteWorksheets = async (
  worksheetIds: Worksheet['id'][],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete('/worksheet/force-delete', { data: worksheetIds, meta })
}

// 휴지통에서 고른 학습지를 영구 삭제한다
export const deleteWorksheetsInTrash = async (
  worksheetIds: Worksheet['id'][],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete('/worksheet/trash/by-worksheet', { data: worksheetIds, meta })
}

// 학원 휴지통 전체를 비운다
export const emptyWorksheetTrash = async (meta?: RequestMeta): Promise<void> => {
  await httpClient().delete('/worksheet/trash/by-academy', { meta })
}

export const restoreWorksheets = async (
  worksheetIds: RestoreWorksheetRequest['worksheetIds'],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<CommonResponse>('/worksheet/restore', { worksheetIds }, { meta })
}

/***************** 학습지 설정 *****************/

export const updateWorksheetAccessModifier = async (
  body: WorksheetAccessModifierBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<CommonResponse>('/worksheet', body, { meta })
}

/** 학습지를 복제하고 새 학습지의 id 를 돌려준다. 이 엔드포인트는 봉투 없이 id 를 바로 낸다 */
export const copyWorksheet = async (
  worksheetId: Worksheet['id'],
  body: WorksheetCopyBody,
  meta?: RequestMeta,
): Promise<Worksheet['id']> =>
  adaptWorksheetId(
    await httpClient().post<CopyCustomWorksheetData>(`/worksheet/${worksheetId}/copy`, body, {
      meta,
    }),
  )

/** 학습지 하나에 배정된 학생들 */
export const getWorksheetAssignees = async (
  worksheetId: Worksheet['id'],
  meta?: RequestMeta,
): Promise<WorksheetAssignee[]> => {
  const body = await httpClient().get<CommonResponseListAssignResponse>(
    `/worksheet/${worksheetId}/assign`,
    { meta },
  )
  return adaptWorksheetAssignees(body.data) ?? []
}

/***************** 학습지 파일 *****************/

/** 이 엔드포인트는 봉투 없이 본문을 바로 낸다 */
export const getWorksheetFile = async (
  params: WorksheetFileParams,
  meta?: RequestMeta,
): Promise<WorksheetFile> =>
  adaptWorksheetFile(
    await httpClient().get<CommonCloudFrontUrlResponse>('/worksheet/v2/download', {
      params,
      meta,
    }),
  )

// HWP 는 비동기로 만들어진다. 요청을 접수한 id 로 파일이 준비됐는지 다시 묻는다.
// 두 엔드포인트 모두 봉투 없이 본문을 바로 낸다.
export const requestWorksheetHwp = async (
  body: WorksheetHwpBody,
  meta?: RequestMeta,
): Promise<WorksheetHwpRequest> =>
  adaptWorksheetHwpRequest(
    await httpClient().post<WorksheetHwpCreationResponse>('/worksheet/hwp', body, { meta }),
  )

export const getWorksheetHwpFiles = async (
  requestId: WorksheetHwpRequest['requestId'],
  meta?: RequestMeta,
): Promise<WorksheetHwpFile[]> =>
  adaptWorksheetHwpFiles(
    await httpClient().get<WorksheetHwpDownloadResponse>(`/worksheet/hwp/download/${requestId}`, {
      meta,
    }),
  ) ?? []

/** 이 엔드포인트는 봉투 없이 본문을 바로 낸다 */
export const getOriginalWorksheetQuota = async (
  meta?: RequestMeta,
): Promise<OriginalWorksheetQuota> =>
  adaptOriginalWorksheetQuota(
    await httpClient().get<OriginalWorksheetQuotaResponse>('/v2/worksheet/original/quota', {
      meta,
    }),
  )

// 원본 학습지 HWP 다운로드의 한도·단가·구매 내역. 세 엔드포인트 모두 봉투 없이 본문을 바로 낸다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 worksheet/controller/WorksheetQuotaController.kt 로 확인.
export const getWorksheetHwpQuota = async (meta?: RequestMeta): Promise<WorksheetHwpQuota> =>
  adaptWorksheetHwpQuota(
    await httpClient().get<OriginalWorksheetHwpDownloadQuotaResponse>(
      '/v2/worksheet/original/hwp/quota',
      { meta },
    ),
  )

export const getWorksheetHwpUnitPrice = async (
  meta?: RequestMeta,
): Promise<WorksheetHwpUnitPrice> =>
  adaptWorksheetHwpUnitPrice(
    await httpClient().get<HwpUnitPriceResponse>('/v2/worksheet/original/hwp/price', { meta }),
  )

// 그 달이 걸친 학원 결제 주기(사용기간) 하나의 내역을 준다
export const getWorksheetHwpPurchaseHistory = async (
  params: WorksheetHwpPurchaseHistoryParams,
  meta?: RequestMeta,
): Promise<WorksheetHwpPurchaseHistory> =>
  adaptWorksheetHwpPurchaseHistory(
    await httpClient().get<HwpPurchaseHistoryResponse>(
      '/v2/worksheet/original/hwp/purchase-history',
      { params, meta },
    ),
  )

/***************** 학습지 디자인 *****************/

export const getWorksheetDesignTemplates = async (
  meta?: RequestMeta,
): Promise<WorksheetDesignTemplate[]> => {
  const body = await httpClient().get<CommonResponseCollectionWorksheetDesignTypeResponse>(
    '/worksheet/design/type',
    { meta },
  )
  return adaptWorksheetDesignTemplates(body.data) ?? []
}

export const getWorksheetDesigns = async (
  params: WorksheetDesignListParams,
  meta?: RequestMeta,
): Promise<Slice<WorksheetDesign>> => {
  const body = await httpClient().get<CommonResponseSliceWorksheetDesignResponse>(
    '/worksheet/design',
    { params, meta },
  )
  return adaptWorksheetDesigns(body.data)
}

export const getWorksheetDesign = async (
  worksheetDesignId: WorksheetDesign['id'],
  meta?: RequestMeta,
): Promise<WorksheetDesignDetail> => {
  const body = await httpClient().get<CommonResponseWorksheetDesignDetailResponse>(
    `/worksheet/design/${worksheetDesignId}`,
    { meta },
  )
  return adaptWorksheetDesignDetail(body.data)
}

export const createWorksheetDesign = async (
  body: WorksheetDesignBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<CommonResponse>('/worksheet/design', body, { meta })
}

export const updateWorksheetDesign = async (
  worksheetDesignId: WorksheetDesign['id'],
  body: WorksheetDesignBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put<CommonResponse>(`/worksheet/design/${worksheetDesignId}`, body, { meta })
}

export const deleteWorksheetDesign = async (
  worksheetDesignId: WorksheetDesign['id'],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<CommonResponse>(`/worksheet/design/${worksheetDesignId}`, { meta })
}

/***************** 과다 사용 캡차 *****************/
// 경로는 /worksheet 아래지만 교재·문제 추천에서 난 캡차 요구도 이 API 로 푼다.
// 두 엔드포인트 모두 봉투 없이 본문을 바로 낸다. 캡차가 필요 없는 상태에서 챌린지를 청하면
// 409(CAPTCHA_NOT_REQUIRED)로 답하고, 그 처리는 호출하는 화면이 한다.

export const issueWorksheetCaptchaChallenge = async (
  meta?: RequestMeta,
): Promise<WorksheetCaptchaChallenge> =>
  adaptWorksheetCaptchaChallenge(
    await httpClient().post<WorksheetCaptchaChallengeResponse>(
      '/worksheet/captcha/challenge',
      undefined,
      { meta },
    ),
  )

export const verifyWorksheetCaptcha = async (
  body: WorksheetCaptchaAnswer,
  meta?: RequestMeta,
): Promise<WorksheetCaptchaVerification> =>
  adaptWorksheetCaptchaVerification(
    await httpClient().post<WorksheetCaptchaVerifyResponse>('/worksheet/captcha/verify', body, {
      meta,
    }),
  )

/***************** 선생님 학습지 목록 *****************/
// 탭마다 봉투가 다르다. 서술형·수능특강·경찰대/사관학교·학교별 기출은 봉투 없이 본문을 바로 내고 나머지는
// CommonResponse 봉투다.

/** 내 학습지(학원·학원그룹 공유 범위, 휴지통) */
export const getMyWorksheets = async (
  params: MyWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<MyWorksheetListItem>> => {
  const body = await httpClient().get<CommonResponseSliceAcademyWorksheetResponse>(
    '/worksheet/academy',
    { params, meta },
  )
  return adaptMyWorksheets(body.data)
}

export const getSchoolPrepareWorksheets = async (
  params: SchoolPrepareWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<LeveledWorksheetListItem>> => {
  const body = await httpClient().get<CommonResponseSliceSchoolPrepareWorksheetResponse>(
    '/worksheet/school-prepare',
    { params, meta },
  )
  return adaptLeveledWorksheets(body.data)
}

export const getSchoolPrepareEssayWorksheets = async (
  params: SchoolPrepareEssayWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<LeveledWorksheetListItem>> =>
  adaptLeveledWorksheets(
    await httpClient().get<SliceSchoolPrepareEssayWorksheetResponse>(
      '/worksheet/school-prepare-essay',
      { params, meta },
    ),
  )

export const getTextbookWorksheets = async (
  params: TextbookWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<TextbookWorksheetListItem>> => {
  const body = await httpClient().get<CommonResponseSliceSchoolPrepareTextbookWorksheetResponse>(
    '/worksheet/school-prepare-textbook',
    { params, meta },
  )
  return adaptTextbookWorksheets(body.data)
}

/** 수능·모의고사(쌍둥이·유사 포함, worksheetTypes 로 가른다) */
export const getPracticeTestWorksheets = async (
  params: PracticeTestWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<PracticeTestWorksheetListItem>> => {
  const body = await httpClient().get<CommonResponseSlicePracticeTestWorksheetResponse>(
    '/worksheet/practice-test',
    { params, meta },
  )
  return adaptPracticeTestWorksheets(body.data)
}

export const getEntranceTestWorksheets = async (
  params: EntranceTestWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<LeveledWorksheetListItem>> => {
  const body = await httpClient().get<CommonResponseSliceEntranceWorksheetResponse>(
    '/worksheet/entrance',
    { params, meta },
  )
  return adaptLeveledWorksheets(body.data)
}

/** 단원TEST·총괄TEST(tag 로 가른다) */
export const getChapterTestWorksheets = async (
  params: ChapterTestWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<LeveledWorksheetListItem>> => {
  const body = await httpClient().get<CommonResponseSliceChapterWorksheetResponse>(
    '/worksheet/chapter',
    { params, meta },
  )
  return adaptLeveledWorksheets(body.data)
}

export const getWeeklyTestWorksheets = async (
  params: WeeklyTestWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<LeveledWorksheetListItem>> => {
  const body = await httpClient().get<CommonResponseSliceWeeklyWorksheetResponse>(
    '/worksheet/weekly',
    { params, meta },
  )
  return adaptLeveledWorksheets(body.data)
}

/** KMM 경시 학습지 */
export const getExamWorksheets = async (
  params: ExamWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<LeveledWorksheetListItem>> => {
  const body = await httpClient().get<CommonResponseSliceExamWorksheetResponse>('/worksheet/exam', {
    params,
    meta,
  })
  return adaptLeveledWorksheets(body.data)
}

/** 수능특강·수능완성 */
export const getSatWorksheets = async (
  params: SatWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<SatWorksheetListItem>> =>
  adaptSatWorksheets(
    await httpClient().get<SliceSatWorksheetResponse>('/worksheet/sat', { params, meta }),
  )

/** 경찰대·사관학교 기출 */
export const getUniversityPracticeTestWorksheets = async (
  params: UniversityPracticeTestWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<PracticeTestWorksheetListItem>> =>
  adaptPracticeTestWorksheets(
    await httpClient().get<SliceUniversityPracticeTestWorksheetResponse>(
      '/worksheet/university-practice-test',
      { params, meta },
    ),
  )

/** 경찰대·사관학교 쌍둥이 */
export const getUniversityPracticeTestPairWorksheets = async (
  params: UniversityPracticeTestPairWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<PracticeTestWorksheetListItem>> =>
  adaptPracticeTestWorksheets(
    await httpClient().get<SliceUniversityPracticeTestWorksheetResponse>(
      '/worksheet/university-practice-test/pair',
      { params, meta },
    ),
  )

/** 초등 연산 학습지(교육과정 묶음 이전의 목록) */
export const getArithmeticWorksheets = async (
  params: ArithmeticWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<ArithmeticWorksheet>> => {
  const body = await httpClient().get<CommonResponseSliceArithmeticWorksheetResponse>(
    '/worksheet/arithmetic',
    { params, meta },
  )
  return adaptArithmeticWorksheets(body.data)
}

/** 교육과정 노드별로 묶은 연산 학습지 */
export const getArithmeticWorksheetGroups = async (
  params: ArithmeticWorksheetGroupListParams,
  meta?: RequestMeta,
): Promise<Slice<ArithmeticWorksheetGroup>> => {
  const body = await httpClient().get<CommonResponseSliceGroupedArithmeticWorksheetResponse>(
    '/worksheet/arithmetic/group',
    { params, meta },
  )
  return adaptArithmeticWorksheetGroups(body.data)
}

export const getSchoolTestSimilarWorksheets = async (
  params: SchoolTestSimilarWorksheetParams,
  meta?: RequestMeta,
): Promise<SchoolTestSimilarWorksheet[]> =>
  adaptSchoolTestSimilarWorksheets(
    await httpClient().get<SchoolTestSimilarWorksheetResponse[]>(
      '/worksheet/school-test/by-school',
      { params, meta },
    ),
  ) ?? []

export const getSchoolTestSimilarWorksheetCount = async (
  params: SchoolTestSimilarWorksheetCountParams,
  meta?: RequestMeta,
): Promise<SchoolTestSimilarWorksheetCount> =>
  adaptSchoolTestSimilarWorksheetCount(
    await httpClient().get<SchoolTestSimilarWorksheetCountResponse>(
      '/worksheet/school-test/count/by-school',
      { params, meta },
    ),
  )

/***************** 예약 출제 *****************/
// 이 컨트롤러는 봉투 없이 본문을 바로 낸다.

/** 주어진 학습지·학생에 걸린 대기 중 예약 대상. 학원 범위라 다른 선생님의 예약도 온다 */
export const getPendingWorksheetAssignScheduleTargets = async (
  body: WorksheetAssignScheduleValidationBody,
  meta?: RequestMeta,
): Promise<WorksheetAssignScheduleTarget[]> =>
  adaptWorksheetAssignScheduleTargets(
    await httpClient().post<WorksheetAssignScheduleTargetResponse[]>(
      '/worksheet-assign-schedule/validation',
      body,
      { meta },
    ),
  ) ?? []

/** 예약 목록. status 를 비우면 전체 탭이다 */
export const getWorksheetAssignSchedules = async (
  params: WorksheetAssignScheduleListParams,
  meta?: RequestMeta,
): Promise<Slice<WorksheetAssignSchedule>> =>
  adaptWorksheetAssignSchedules(
    await httpClient().get<SliceWorksheetAssignScheduleDetailResponse>(
      '/worksheet-assign-schedule/targets',
      { params, meta },
    ),
  )

export const getWorksheetAssignScheduleCount = async (
  meta?: RequestMeta,
): Promise<WorksheetAssignScheduleCount> =>
  adaptWorksheetAssignScheduleCount(
    await httpClient().get<WorksheetAssignScheduleCountResponse>(
      '/worksheet-assign-schedule/counts',
      { meta },
    ),
  )

export const createWorksheetAssignSchedule = async (
  body: WorksheetAssignScheduleBody,
  meta?: RequestMeta,
): Promise<WorksheetAssignScheduleCreation> =>
  adaptWorksheetAssignScheduleCreation(
    await httpClient().post<CreateWorksheetAssignScheduleResponse>(
      '/worksheet-assign-schedule',
      body,
      { meta },
    ),
  )

export const rescheduleWorksheetAssignSchedule = async (
  batchId: WorksheetAssignSchedule['batchId'],
  body: WorksheetAssignScheduleTimeBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch(`/worksheet-assign-schedule/${batchId}`, body, { meta })
}

export const cancelWorksheetAssignSchedule = async (
  batchId: WorksheetAssignSchedule['batchId'],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete(`/worksheet-assign-schedule/${batchId}`, { meta })
}

export const cancelWorksheetAssignScheduleStudents = async (
  batchId: WorksheetAssignSchedule['batchId'],
  body: WorksheetAssignScheduleStudentsCancelBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete(`/worksheet-assign-schedule/${batchId}/students`, {
    data: body,
    meta,
  })
}

/** 기존 예약에 학생을 더하고 늘어난 전체 대상 수를 돌려준다. 이미 있는 학생이 섞이면 요청 전체가 거부된다 */
export const addWorksheetAssignScheduleStudents = async (
  batchId: WorksheetAssignSchedule['batchId'],
  body: WorksheetAssignScheduleStudentsAddBody,
  meta?: RequestMeta,
): Promise<Pick<WorksheetAssignScheduleCreation, 'targetCount'>> =>
  adaptWorksheetAssignScheduleTargetCount(
    await httpClient().post<AddWorksheetAssignScheduleTargetResponse>(
      `/worksheet-assign-schedule/${batchId}/students`,
      body,
      { meta },
    ),
  )

/***************** 이메일로 보내기 *****************/
// 받는 사람 id 는 직접 입력한 주소면 없다. saveFlag 를 켜면 학생 받는 사람의 주소를 학생 정보에 저장한다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 email/controller/EmailController.kt·request/EmailRequest.kt 로 확인.
export type WorksheetEmailBody = EmailRequest

export const sendWorksheetsByEmail = async (
  body: WorksheetEmailBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<EmailSendData>('/email', body, { meta })
}

/***************** 다른 기관 학습지 (검색 서버) *****************/
// 트래킹 이벤트명은 meta 로 넘기므로 생성 Params 의 mp_event_name 은 뺀다.
export type SharedCustomWorksheetSearchParams = Omit<SearchParams, 'mp_event_name'>

export const getSharedCustomWorksheets = async (
  params: SharedCustomWorksheetSearchParams,
  meta?: RequestMeta,
): Promise<{ content: SharedCustomWorksheet[]; last: boolean }> => {
  const body = await httpClient('search').get<SearchData>('/v1/shared-custom-worksheets', {
    params,
    meta,
  })
  return adaptSharedCustomWorksheets(body)
}

/** 캡차 문제 이미지. JWT 헤더 인증이라 <img src> 로 직접 열 수 없어 파일로 받는다 */
export const getWorksheetCaptchaImage = async (
  challengeId: WorksheetCaptchaChallenge['challengeId'],
  meta?: RequestMeta,
): Promise<HttpFile> => httpClient().getFile(`/worksheet/captcha/${challengeId}/image`, { meta })

/**
 * 학습지를 소유한 학원의 브랜드. 로그인 전 QR 진입에서 부르므로 토큰 없이 보내고,
 * 실패는 호출부가 기본 동작으로 넘기므로 공통 오류 토스트를 막는다.
 */
export const getWorksheetBrand = async (
  worksheetId: Worksheet['id'],
  signal?: AbortSignal,
): Promise<Brand | null> => {
  const body = await httpClient().get<unknown>(`/worksheet/${worksheetId}/brand`, {
    withoutAuth: true,
    preventErrorToast: true,
    signal,
  })
  return adaptWorksheetBrand(body)
}
